import { useEffect, useRef } from "react"
import styled from "styled-components"
import SkeletonLoader from "./SkeletonLoader"
import useElementSize from "../hooks/useElementSize"
import MobileNavMenu from "./MobileNavMenu"
import { LocationIcon } from "./Icons"
import HeaderMetrics from "./HeaderMetrics"
import { useToast } from "../context/ToastContext"
import logoCokeHeader from "../assets/header-logo-coke-only.svg"
import logoCokePeru from "../assets/header-logo-coke-peru.png"
import logoAlmaceniaWhite from "../assets/logo-almacenia-white.svg"
import { normalizeCountry } from "../utils/normalizeCountry"
import { ACTIVE_HEADER_VARIANT, HEADER_VARIANTS } from "../config/headerTheme"
import {
  IMAGE_UPLOAD_ACCEPT,
  USER_AVATAR_IMAGE_RULES,
  validateImageUpload,
} from "../utils/imageUploadValidation"

const Container = styled.div`
  --header-primary: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].primary};
  --header-accent: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].accent};
  --header-primary-light: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].primaryLight};
  --header-text: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].text};
  --header-text-dark: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].textDark};
  --header-avatar-border: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].avatarBorder};
  --header-inactive: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].inactive};
  --header-divider: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].divider};

  padding: 15px 15px 0 15px;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  justify-content: center;
  margin-bottom: 0px;
`

const ProfileBox = styled.div`
  width: 100%;
  border-radius: 15px;
  background: var(--header-primary);
  padding: 20px;
  position: relative;
  min-height: 132px;
  height: auto;
`

const ProfileHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;
`

const LogoBrand = styled.div`
  min-height: 48px;
`

const HeaderActions = styled.div`
  position: relative;
  min-width: 104px;
  min-height: 25px;

  @media (max-width: 360px) {
    min-width: 92px;
  }
`

const ButtonMarketplace = styled.div`
  height: 25px;
  display: grid;
  grid-template-columns: ${({ $isHost }) => $isHost ? "1fr 35px" : "35px"};
  justify-content: flex-end;
  align-items: start;
`

const DropdownContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const HiperLinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const HiperLink = styled.a`
  border: 1px solid white;
  color: white;
  background-color: transparent;
  font-weight: 500;
  border-radius: 6px;
  padding: 1px 8px;
  font-size: 13px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
`

const ProfileAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 100%;
  position: absolute;
  background: white;
  left: 0;
  right: 0;
  bottom: -40px;
  margin: auto;
  border: 2px solid var(--header-avatar-border);
  overflow: hidden;

  @media (max-width: 400px) {
    width: 90px;
    height: 90px;
    bottom: -30px;
  }
`

const ProfileAvatarImg = styled.img`
  object-fit: contain;
  object-position: center;
  aspect-ratio: 1 / 1;
  width: 100%;
  height: 100%;
`

const ViewTitle = styled.h1`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  margin: ${({ $hasAvatar }) => $hasAvatar ? "50px 0 6px" : "12px 0 6px"};
  padding: 0 16px;
  color: #111;
  font-weight: 600;
  font-size: 20px;
  text-align: center;
`

const HeaderAvatarSpacer = styled.div`
  height: 50px;
`

const ProfileName = styled.div`
  text-align: center;
  margin-top: 20px;

  h1 {
    font-size: 22px;
    font-weight: 700;
    margin:0;
  }
`

const RestoWrapper = styled.div`
  .address-restaurant {
    display: grid;
    grid-template-columns: auto auto;
    gap: 5px;
    align-items: center;
    justify-content: center;
  }

  .address-restaurant-item h6 {
    margin: 0;
    color: #8d8e8e;
    font-weight: 500;
    font-size: 18px;
  }

  .location-restaurant {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 5px;

    p {
      text-align: center;
      font-size: 14px;
      color: #afafaf;
      margin: 0;
    }
  }
`

const AlmaceniaBrand = styled.div`
  position: absolute;
  top: 16px;
  left: 20px;
  width: 140px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
`

const AlmaceniaLogoImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`

const CokeBrand = styled.div`
  position: absolute;
  bottom: 16px;
  right: 20px;
  width: 96px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;

  @media (max-width: 360px) {
    width: 92px;
    right: 16px;
  }
`

const CokeLogo = styled.img`
  display: block;
  width: 100%;
  height: auto;
`

const InputFile = styled.input`
  display:none;
`

const LabelInputFile = styled.label``

const Header = ({
  type = "",
  userActive = false,
  restoName = "",
  restoAddress = "",
  welcome = false,
  likes = 0,
  points = 0,
  name = "",
  avatar = "",
  bannerImage = "",
  onImageUpload,
  onAvatar,
  loader = false,
  profile = true,
  resto = false,
  title = "",
  showName = true,
  showPoints = true,
  showNiamPoinst = true,
  showHostPoints = false,
  showRestoAddress = true,
  showRestoName = true,
  labelLikes = "Puntos",
  labelPoints = "Ñamis",
  onWindowSize = () => { },
  logout = () => { },
  logoCountry = "",
  menuTabs = [],
  onSelectMenuTab = () => {},
  loaderPoints = false,
  loaderLikes = false,
  onChangeAvatar = null,
  hideSpacer = false,
  ...props
}) => {
  const containerRef = useRef(null)
  const { width, height } = useElementSize(containerRef)
  const toast = useToast()
  const isHost = type === "host"
  const normalizedLogoCountry = normalizeCountry(logoCountry)
  const isPeruCokeLogo = normalizedLogoCountry === "peru" || normalizedLogoCountry === "pe"
  const cokeHeaderLogo = isPeruCokeLogo ? logoCokePeru : logoCokeHeader
  const displayAvatar = bannerImage || avatar || ""
  const showInlineMetrics = profile && (showPoints || showNiamPoinst)

  useEffect(() => onWindowSize(width, height), [width, height])

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validation = await validateImageUpload(file, USER_AVATAR_IMAGE_RULES)
    if (!validation.ok) {
      toast?.error(validation.error)
      event.target.value = ""
      return
    }

    try {
      if (onImageUpload) {
        await onImageUpload(file)
        if (onChangeAvatar) onChangeAvatar()
      }
    } catch (error) {
      toast?.error(error?.message || "No se pudo actualizar la imagen.")
    } finally {
      event.target.value = ""
    }
  }

  return (
    <Container type={type} ref={containerRef} {...props}>
      <ProfileBox className="profile-box" $profile={profile}>
        <ProfileHeader>
          <LogoBrand />

          <HeaderActions>
            <ButtonMarketplace $isHost={isHost}>
              {isHost && (
                <HiperLinkContainer>
                  <HiperLink target="_blank" rel="noreferrer" href="https://mercado.ñam.org">
                    canjear chapas
                  </HiperLink>
                </HiperLinkContainer>
              )}
              <DropdownContainer>
                <MobileNavMenu tabs={menuTabs} onSelectTab={onSelectMenuTab} />
              </DropdownContainer>
            </ButtonMarketplace>
          </HeaderActions>
        </ProfileHeader>

        <AlmaceniaBrand>
          <AlmaceniaLogoImg src={logoAlmaceniaWhite} alt="Almacenia" />
        </AlmaceniaBrand>

        <CokeBrand $isPeru={isPeruCokeLogo}>
          <CokeLogo src={cokeHeaderLogo} alt={isPeruCokeLogo ? "Coca-Cola Perú" : "Coca-Cola"} />
        </CokeBrand>

        {displayAvatar && (
          <ProfileAvatar>
            <SkeletonLoader active={loader} width="120px" height="120px">
              <LabelInputFile htmlFor="fileInput">
                <ProfileAvatarImg src={displayAvatar} alt={name || "avatar"} />
              </LabelInputFile>
              <InputFile id="fileInput" type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={handleImageChange} />
            </SkeletonLoader>
          </ProfileAvatar>
        )}
      </ProfileBox>

      {title && <ViewTitle $hasAvatar={Boolean(displayAvatar)}>{title}</ViewTitle>}

      {displayAvatar && !title && !showInlineMetrics && !hideSpacer && <HeaderAvatarSpacer />}

      {showInlineMetrics && (
        <HeaderMetrics
          type={type}
          likes={likes}
          points={points}
          labelLikes={labelLikes}
          labelPoints={labelPoints}
          loaderLikes={loaderLikes}
          loaderPoints={loaderPoints}
          showPoints={showPoints}
          showNiamPoinst={showNiamPoinst}
        />
      )}

      {showName && type !== "resto" && (
        <ProfileName>
          <SkeletonLoader active={loader} width="80%" height="30px">
            <h1>
              {name || "Usuario"}
            </h1>
          </SkeletonLoader>
        </ProfileName>
      )}

      {userActive && resto && (
        <RestoWrapper>
          <div className="address-restaurant">
            <div className="address-restaurant-item">
              <LocationIcon width="20" height="20" color="var(--header-accent)" />
            </div>
            <div className="address-restaurant-item">
              <SkeletonLoader active={loader} width="50%" height="30px">
                <h6>{restoName || "Restaurante"}</h6>
              </SkeletonLoader>
            </div>
          </div>
          {showRestoAddress && (
            <div className="location-restaurant">
              <SkeletonLoader active={loader} width="80%" height="30px">
                <p>{restoAddress || "Dirección no disponible"}</p>
              </SkeletonLoader>
            </div>
          )}
        </RestoWrapper>
      )}
    </Container>
  )
}

export default Header
